import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ClockCounterClockwiseIcon, ArrowCounterClockwiseIcon } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { listVersions, restoreVersion } from "@/lib/api";

export default function VersionHistory({ ingredientId, onRestored }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);

  const load = () => {
    setLoading(true);
    listVersions(ingredientId).then(setVersions).finally(() => setLoading(false));
  };
  useEffect(load, [ingredientId]); // eslint-disable-line

  const handleRestore = async (vid) => {
    setRestoring(vid);
    try {
      await restoreVersion(ingredientId, vid);
      toast.success("Version restored");
      onRestored?.();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally { setRestoring(null); }
  };

  if (loading) return <div className="font-mono text-xs text-[var(--text-muted)] p-6">Loading history…</div>;

  if (!versions.length) {
    return (
      <div className="border border-dashed border-[var(--border)] p-10 text-center" data-testid="versions-empty">
        <ClockCounterClockwiseIcon size={22} className="mx-auto text-[var(--text-muted)]" />
        <div className="mt-2 font-mono text-xs text-[var(--text-muted)]">No prior versions. Edits will be recorded here.</div>
      </div>
    );
  }

  return (
    <div className="border border-[var(--border)]" data-testid="versions-list">
      <table className="w-full text-sm">
        <thead>
          <tr>
            {["VERSION", "REASON", "WHEN", "NAME", ""].map((h, i) => (
              <th key={i} className="label-kicker px-4 py-3 border-b border-[var(--border)] bg-[var(--surface)] text-left">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {versions.map((v) => (
            <tr key={v.id} className="cell-border" data-testid={`version-row-${v.id}`}>
              <td className="px-4 py-3 font-mono">v{v.version}</td>
              <td className="px-4 py-3 font-mono text-xs text-[var(--text-muted)]">{v.reason}</td>
              <td className="px-4 py-3 font-mono text-xs">{new Date(v.created_at).toLocaleString()}</td>
              <td className="px-4 py-3 font-mono text-xs truncate max-w-[300px]">{v.snapshot?.ingredientName}</td>
              <td className="px-4 py-3 text-right">
                <Button
                  size="sm" variant="outline"
                  onClick={() => handleRestore(v.id)}
                  disabled={restoring === v.id}
                  className="rounded-none h-8 font-mono text-[10px] uppercase tracking-[0.14em]"
                  data-testid={`version-restore-${v.id}`}
                >
                  <ArrowCounterClockwiseIcon size={12} className="mr-1" />
                  {restoring === v.id ? "Restoring…" : "Restore"}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
